/**
 * Asking for a revision of the reader in use (#1428; R1 of docs/plans/2026-10-14_process-lags-and-moulding-inputs.md).
 *
 * A platform admin asks the author for the next version of a reader in use, with a note and answers to its questions,
 * as a send-back. The version stays in use until the next one is approved; approving it reads the files the old version
 * read again when it adds readings. A revision only adds (new columns, new measurements streams): the server refuses at
 * approval one that changes how an existing column is read, since an imported reading is written once. Any reader may be
 * revised (R3 of docs/plans/2026-10-19_reader-layout-change.md): its heats, lots and products stay as they are, and its
 * dry run reads the earlier file with both versions, which must read it the same (R4, previousText).
 */
import type { ImportExtractorVersion, ImportJob } from "@/lib/api/imports";
import { plural } from "./review";
import { answersBody, type Answers, type SendBackState } from "./send-back";

/** Where a reader stands for a revision: nothing to revise (none in use, or a draft waits for review), a revision was
 *  asked (the note it was asked with, kept on the version in use until its draft is approved or its job cancelled), or
 *  one can be asked. */
export type RevisionState =
  | { kind: "none" }
  | { kind: "asked"; active: ImportExtractorVersion; note: string }
  | { kind: "can_ask"; active: ImportExtractorVersion };

export function revisionStateOf(versions: readonly ImportExtractorVersion[]): RevisionState {
  const active = versions.find((v) => v.status === "active");
  if (!active || versions.some((v) => v.status === "draft")) return { kind: "none" };
  // The version in use carries a note only while a revision of it is asked: an approval clears it.
  const note = (active.review_note ?? "").trim();
  return note ? { kind: "asked", active, note } : { kind: "can_ask", active };
}

/** Whether the request can be sent, and if not, why. A revision always says what to change in its note (the reader's
 *  card shows it while the revision is open); a correction says what is right instead. */
export function revisionFormState(note: string, answers: Answers): SendBackState {
  if (answersBody(answers).some((a) => !a.keep && !a.text)) {
    return { ok: false, reason: "Write each correction, or keep that assumption.", blankCorrection: true };
  }
  if (!note.trim()) return { ok: false, reason: "Say what the revision should change.", blankCorrection: false };
  return { ok: true, reason: "", blankCorrection: false };
}

/** Whether a draft was written for a revision of the version in use: its author's job is a revision job. */
export function isRevisionDraft(draft: Pick<ImportExtractorVersion, "author_job_id">, jobs: readonly ImportJob[]): boolean {
  return !!draft.author_job_id && jobs.some((j) => j.id === draft.author_job_id && j.kind === "revision");
}

/** What approving a revision draft does, said before it is approved. */
export function revisionApprovalText(inUse: number, next: number): string {
  return (
    `A revision of v${inUse}, the version in use. Approving puts v${next} in use in its place and reads the files ` +
    `v${inUse} read again with it, adding what is new to its readings; heats, lots and products are not read again. ` +
    `A change to how an existing column is read is refused.`
  );
}

/** What the draft's dry run found on the earlier files of the version it revises (validation.report.previous, R4): each
 *  file read the same, or read differently (the approval refuses it), or nothing to compare and why; "" for a draft
 *  that revises nothing. */
export function previousText(validation: ImportExtractorVersion["validation"]): string {
  const report = validation?.report;
  const p = report && typeof report === "object" ? (report as { previous?: unknown }).previous : undefined;
  if (!p || typeof p !== "object") return "";
  const { compared, same, note, summary, files } = p as {
    compared?: boolean;
    same?: boolean;
    note?: string;
    summary?: string;
    files?: {
      file?: string;
      compared?: boolean;
      same?: boolean;
      note?: string;
      streams?: Record<string, { rows?: number }>;
    }[];
  };
  // A file that was not compared is named with why, whatever the others read (#1595 re-review N1).
  const skipped = (files ?? [])
    .filter((f) => !f.compared)
    .map((f) => ` "${f.file ?? "A file"}" was not compared: ${(f.note ?? "").trim() || "no reason given"}.`)
    .join("");
  if (!compared) {
    const why = (note ?? "").trim();
    return why ? `${why.charAt(0).toUpperCase()}${why.slice(1)}.` : "";
  }
  if (!same) {
    return `The draft reads the earlier files of the version in use differently${summary ? `: ${summary}` : ""}.${skipped}`;
  }
  const read = (files ?? []).filter((f) => f.compared && f.same);
  const names = read.map((f) => `"${f.file ?? "a file"}"`).join(" and ");
  const all = read.flatMap((f) => Object.values(f.streams ?? {}));
  const rows = all.reduce((n, s) => n + (s.rows ?? 0), 0);
  const which = read.length === 1 ? `${names}, the earlier file of the version in use,` : `${names}, the earlier files of the version in use,`;
  return `The draft reads ${which} the same: ${plural(rows, "row")}.${skipped}`;
}

/** The approval's answer about the old version's other files: how many are queued to be read again, and which could not
 *  be; "" when there was none to queue. */
export function reapplyText(reapply: { queued: number; skipped?: string[] } | undefined): string {
  if (!reapply) return "";
  const skipped = reapply.skipped ?? [];
  const parts: string[] = [];
  if (reapply.queued > 0) parts.push(`${plural(reapply.queued, "other file")} queued to be read again.`);
  if (skipped.length > 0) parts.push(`Not read again: ${skipped.join("; ")}.`);
  return parts.join(" ");
}
